import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getEstateById, fetchHouses } from "../services/api";
import { formatUrl } from "../utils/config";
import { formatPrice } from "../utils/format";
import { stripHtml } from "../utils/text";

const EstateDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [estate, setEstate] = useState(location.state?.estate || location.state?.estateData);
  const [loading, setLoading] = useState(!(
    location.state?.estate || location.state?.estateData
  ));
  const [mainImage, setMainImage] = useState("");
  const [estateHouses, setEstateHouses] = useState([]);
  const [housesLoading, setHousesLoading] = useState(true);

  useEffect(() => {
    const loadEstate = async () => {
      if (!estate) {
        try {
          const res = await getEstateById(id);
          // Some APIs wrap payload differently; support both
          const data = res?.payload || res;
          setEstate(data);
          setMainImage(data?.thumbnail || "");
        } catch (error) {
          console.error("Error loading estate:", error);
          navigate("/estates", { replace: true });
        } finally {
          setLoading(false);
        }
      } else {
        setMainImage(estate?.thumbnail || "");
      }
    };

    loadEstate();
  }, [id, estate, navigate]);

  // Load houses within this estate
  useEffect(() => {
    const loadEstateHouses = async () => {
      if (!id) return;
      setHousesLoading(true);
      try {
        const res = await fetchHouses(0, 50, { estateId: id });
        const page = res?.payload?.page || [];
        setEstateHouses(Array.isArray(page) ? page : []);
      } catch (error) {
        console.error("Error loading houses for estate:", error);
        setEstateHouses([]);
      } finally {
        setHousesLoading(false);
      }
    };

    loadEstateHouses();
  }, [id]);

  const createMarkup = (html) => ({ __html: html });

  if (loading) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!estate) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>Estate not found</h3>
              <p>The estate you are looking for does not exist or has been removed.</p>
              <button className="th-btn" onClick={() => navigate("/estates")}>Back to Estates</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{estate?.name || "Estate Details"}</title>
        <meta name="description" content={estate?.description || "Estate details"} />
      </Helmet>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">{estate?.name}</h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/estates">Estates</Link>
              </li>
              <li>{estate?.name}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="property-main-image mb-4">
                <img
                  src={formatUrl(mainImage) || "/assets/img/default-offer.png"}
                  alt={estate?.name || "Estate"}
                  className="img-fluid rounded w-100"
                  style={{ height: "500px", objectFit: "cover" }}
                  onError={(e) => (e.currentTarget.src = "/assets/img/default-offer.png")}
                />
              </div>

              {Array.isArray(estate?.photos) && estate.photos.length > 0 && (
                <div className="property-thumbnails mb-5">
                  <div className="row g-3">
                    <div className="col-auto">
                      <button
                        className={`thumbnail-btn ${mainImage === estate.thumbnail ? "active" : ""}`}
                        onClick={() => setMainImage(estate.thumbnail)}
                      >
                        <img
                          src={formatUrl(estate.thumbnail)}
                          alt="Thumbnail"
                          className="img-fluid"
                          style={{ width: "80px", height: "60px", objectFit: "cover" }}
                        />
                      </button>
                    </div>
                    {estate.photos.map((photo, index) => (
                      <div className="col-auto" key={index}>
                        <button
                          className={`thumbnail-btn ${mainImage === photo ? "active" : ""}`}
                          onClick={() => setMainImage(photo)}
                        >
                          <img
                            src={formatUrl(photo)}
                            alt={`Thumbnail ${index + 1}`}
                            className="img-fluid"
                            style={{ width: "80px", height: "60px", objectFit: "cover" }}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="d-flex my-3">
                {estate?.design && (
                  <div className="estate-thumbnail flex-fill">
                    <video
                      width={"100%"}
                      height={"200px"}
                      src={formatUrl(estate.design)}
                      controls={true}
                      volume={0}
                      autoPlay={false}
                    />
                  </div>
                )}

                {estate?.coordinates && (
                  <div className="estate-thumbnail ps-2 flex-fill">
                    <iframe
                      src={estate.coordinates}
                      width="100%"
                      height={"200px"}
                      style={{ border: "0" }}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                )}
              </div>

              <div className="property-details mb-5">
                <h2 className="h3 mb-3">{estate?.name}</h2>
                <p className="text-muted mb-4">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  {(estate?.district ? estate.district + ", " : "")}
                  {estate?.lga || estate?.city || ""}
                  {estate?.state ? ", " + estate.state : ""}
                </p>

                <div className="property-features mb-4">
                  <div className="row g-3">
                    {estate?.sizeSqm && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-ruler-combined fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{estate.sizeSqm} sqm</h6>
                        </div>
                      </div>
                    )}
                    {/* {estate?.type && (
                      <div className="col-md-3 col-6">
                        <div className="feature-box text-center p-3 border rounded">
                          <i className="fas fa-building fa-2x text-primary mb-2"></i>
                          <h6 className="mb-0">{estate.type}</h6>
                        </div>
                      </div>
                    )} */}
                  </div>
                </div>

                {estate?.description && (
                  <div className="property-description mb-5">
                    <h4 className="h5 mb-3">Description</h4>
                    <p>{stripHtml(estate.description)}</p>
                  </div>
                )}

                {estate?.features && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Features</h4>
                    <div className="row g-1" dangerouslySetInnerHTML={createMarkup(estate.features)} />
                  </div>
                )}

                {/* Houses in this Estate */}
                <div className="property-estate-houses mb-5">
                  <h4 className="h5 mb-4">Houses in this Estate</h4>
                  {housesLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : estateHouses.length === 0 ? (
                    <p className="text-muted">No houses found in this estate.</p>
                  ) : (
                    <>
                      <div className="row gy-4 mt-2">
                        {estateHouses.slice(0, 2).map((property) => (
                          <div key={property.id} className="col-md-6">
                          <div className="project-card style1-1 property-card-hover">
                            <div className="project-card-img">
                              <img
                                src={formatUrl(property?.thumbnail) || "/assets/img/main/default.jpg"}
                                alt={property?.title || `${property?.houseNumber} - ${property?.buildingType}`}
                                className="w-100"
                                onError={(e) => (e.currentTarget.src = "/assets/img/main/default.jpg")}
                              />
                            </div>
                            <div
                              className="project-card-details"
                              style={{ transition: "all 0.3s ease-in-out" }}
                            >
                              <h4 className="box-title">
                                <Link
                                  to={`/properties/${property.id}`}
                                  state={{ property }}
                                  style={{
                                    color: "inherit",
                                    textDecoration: "none",
                                    transition: "color 0.3s ease-in-out",
                                  }}
                                >
                                  {property?.houseNumber} - {property?.buildingType}
                                </Link>
                              </h4>
                              <p
                                className="mb-2"
                                style={{ transition: "color 0.3s ease-in-out" }}
                              >
                                <i
                                  className="fas fa-map-marker-alt me-2"
                                  style={{ color: "inherit" }}
                                ></i>
                                {(property?.estate?.name || estate?.name) || "Estate"}
                                {property?.blockNumber ? `, Block ${property.blockNumber}` : ""}
                              </p>
                              <div
                                className="property-features mb-3 d-flex flex-wrap gap-2"
                                style={{ transition: "color 0.3s ease-in-out" }}
                              >
                                {typeof property?.bedRooms === "number" && (
                                  <span className="me-3">
                                    <i className="fas fa-bed me-1"></i> {property.bedRooms} Beds
                                  </span>
                                )}
                                {typeof property?.toilets === "number" && (
                                  <span className="me-3">
                                    <i className="fas fa-toilet me-1"></i> {property.toilets} Baths
                                  </span>
                                )}
                                {property?.livingRoom > 0 && (
                                  <span className="me-3">
                                    <i className="fas fa-couch me-1"></i> {property.livingRoom} Living
                                  </span>
                                )}
                                {property?.kitchen > 0 && (
                                  <span className="me-3">
                                    <i className="fas fa-utensils me-1"></i> {property.kitchen} Kitchen
                                  </span>
                                )}
                                {property?.dianning > 0 && (
                                  <span>
                                    <i className="fas fa-chair me-1"></i> {property.dianning} Dining
                                  </span>
                                )}
                              </div>
                              <Link
                                to={`/properties/${property.id}`}
                                state={{ property }}
                                className="th-btn style9"
                                style={{ transition: "all 0.3s ease-in-out" }}
                              >
                                View Details <i className="fas fa-arrow-right ms-1" />
                              </Link>
                            </div>
                          </div>
                          </div>
                        ))}
                      </div>
                      {estateHouses.length > 2 && (
                        <div className="text-center mt-4">
                          <Link
                            to={`/estates/${id}/houses`}
                            state={{ estate }}
                            className="th-btn"
                          >
                            View All Houses
                            <i className="fas fa-arrow-right ms-2"></i>
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="property-sidebar">
                <div className="estate-info bg-light p-4 rounded mb-4">
                  <h4 className="h5 mb-3">Estate Information</h4>
                  {estate?.thumbnail && (
                    <div className="estate-thumbnail mb-3">
                      <img
                        src={formatUrl(estate.thumbnail)}
                        alt={estate?.name}
                        className="img-fluid rounded"
                        style={{ height: "150px", width: "100%", objectFit: "cover" }}
                      />
                    </div>
                  )}

                  <ul className="list-unstyled mb-0">
                    {estate?.name && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Name:</span>
                        <strong>{estate.name}</strong>
                      </li>
                    )}
                    {estate?.type && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Type:</span>
                        <strong>{estate.type}</strong>
                      </li>
                    )}
                    {/* {(estate?.district || estate?.lga || estate?.state || estate?.city) && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Location:</span>
                        <strong>
                          {(estate?.district ? estate.district + ", " : "")}
                          {estate?.lga || estate?.city || ""}
                          {estate?.state ? ", " + estate.state : ""}
                        </strong>
                      </li>
                    )} */}
                    {estate?.status && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Status:</span>
                        <strong className={(estate.status === "AVAILABLE" || estate.status === "ACTIVE") ? "text-success" : "text-danger"}>
                          {estate.status}
                        </strong>
                      </li>
                    )}
                    {(typeof estate?.availableHouses === "number") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Available Houses:</span>
                        <strong>{estate.availableHouses}</strong>
                      </li>
                    )}
                    {(typeof estate?.availablePlots === "number") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Available Plots:</span>
                        <strong>{estate.availablePlots}</strong>
                      </li>
                    )}
                    {estate?.acquisitionType && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Acquisition Type:</span>
                        <strong>{estate.acquisitionType}</strong>
                      </li>
                    )}
                    {(estate?.acquisitionCost !== undefined && estate?.acquisitionCost !== null && estate?.acquisitionCost !== "") && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Acquisition Cost:</span>
                        <strong>{formatPrice(Number(estate.acquisitionCost) || 0)}</strong>
                      </li>
                    )}
                    {Array.isArray(estate?.approvals) && (
                      <li className="d-flex justify-content-between py-2">
                        <span>Approvals:</span>
                        <strong>{estate.approvals.length}</strong>
                      </li>
                    )}
                  </ul>
                  {Array.isArray(estate?.approvals) && estate.approvals.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">Approvals available.</small>
                    </div>
                  )}
                  {estate?.planUrl && (
                    <div className="mt-3">
                      <a
                        href={formatUrl(estate.planUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary w-100 text-decoration-none"
                      >
                        <i className="fas fa-file-alt me-2"></i> View Plan
                      </a>
                    </div>
                  )}
                </div>

                <div className="property-actions d-flex flex-column gap-3 mb-3">
                  <Link to="/contact" className="btn btn-primary w-100 text-decoration-none">
                    <i className="fas fa-envelope me-2"></i> Contact Us
                  </Link>
                  <Link to="tel:+2347055558942" className="btn btn-warning w-100 text-decoration-none">
                    <i className="fas fa-phone me-2"></i> Call Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EstateDetails;
